import { useCallback, useEffect, useRef } from 'react';
import { useI18n } from '../i18n';
import { useSpeechRecognition } from './useSpeechRecognition';
import { assistantVoiceErrorCopy } from './voiceErrorMessage';
import styles from './VoiceSearchControls.module.css';

type VoiceSearchControlsProps = {
  onTranscript: (text: string) => void;
  /** Called once when a final utterance ends (browser STT) or cloud returns text. */
  onFinalUtterance?: (text: string) => void;
  /** Compact mic next to the search input (Phase 1). */
  variant?: 'stacked' | 'inline';
};

export function VoiceSearchControls({
  onTranscript,
  onFinalUtterance,
  variant = 'stacked',
}: VoiceSearchControlsProps) {
  const { t, language } = useI18n();
  const speech = useSpeechRecognition(language);
  const wasListeningRef = useRef(false);
  const submittedRef = useRef<string | null>(null);
  const inline = variant === 'inline';

  const listening = speech.state === 'listening' || speech.state === 'processing';

  useEffect(() => {
    if (listening) {
      wasListeningRef.current = true;
      submittedRef.current = null;
    }
  }, [listening]);

  useEffect(() => {
    const live = speech.interimTranscript.trim() || speech.transcript.trim();
    if (!live) {
      return;
    }
    onTranscript(live);
  }, [speech.interimTranscript, speech.transcript, onTranscript]);

  useEffect(() => {
    const text = speech.transcript.trim();
    if (!wasListeningRef.current || speech.state !== 'idle' || !text) {
      return;
    }
    if (submittedRef.current === text) {
      return;
    }
    submittedRef.current = text;
    wasListeningRef.current = false;
    onFinalUtterance?.(text);
  }, [speech.state, speech.transcript, onFinalUtterance]);

  const onMicClick = useCallback(() => {
    if (listening) {
      speech.stop();
      return;
    }
    void speech.start();
  }, [listening, speech]);

  const errorCopy = speech.errorCode ? assistantVoiceErrorCopy(speech.errorCode) : null;
  const live =
    speech.interimTranscript.trim() || (listening ? t('assistant.voice.listening') : '');

  if (speech.backend === 'unavailable') {
    if (inline) {
      return null;
    }
    return (
      <div className={styles.wrap}>
        <p className={styles.hint} role="status">
          {t('assistant.voice.unsupported')}
        </p>
      </div>
    );
  }

  const micButton = (
    <button
      type="button"
      className={`${styles.micBtn}${listening ? ` ${styles.micBtnActive}` : ''}`}
      aria-pressed={listening}
      aria-label={listening ? t('assistant.voice.stop') : t('assistant.voice.start')}
      title={t('assistant.voice.start')}
      onClick={onMicClick}
    >
      <MicIcon />
    </button>
  );

  if (inline) {
    return (
      <div className={styles.inlineWrap}>
        {micButton}
        {listening ? (
          <p className={styles.liveInline} aria-live="polite">
            {t('assistant.voice.listening')}
          </p>
        ) : null}
        {errorCopy && !listening ? (
          <p className={styles.errorInline} role="alert">
            {t(errorCopy.key, errorCopy.params)}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        {micButton}
        {live ? (
          <p className={styles.live} aria-live="polite">
            {live}
          </p>
        ) : null}
      </div>
      {errorCopy ? (
        <p className={styles.error} role="alert">
          {t(errorCopy.key, errorCopy.params)}
        </p>
      ) : (
        <p className={styles.hint}>{t('assistant.voice.hint')}</p>
      )}
    </div>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden fill="currentColor">
      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />
    </svg>
  );
}
